import { useReducer, useCallback } from 'react';

interface CmsBlockSnapshot {
  id: string;
  blockDefinitionId: string | null;
  sortOrder: number;
  props: Record<string, unknown>;
  parentBlockId?: string | null;
  loyaltyMembersOnly?: boolean;
}

interface HistoryState {
  stack: CmsBlockSnapshot[][];
  cursor: number;
}

type HistoryAction =
  | { type: 'PUSH'; blocks: CmsBlockSnapshot[] }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESET'; blocks: CmsBlockSnapshot[] };

const MAX_HISTORY = 50;

function historyReducer(state: HistoryState, action: HistoryAction): HistoryState {
  switch (action.type) {
    case 'PUSH': {
      // Discard any redo states after current cursor, then append
      const truncated = state.stack.slice(0, state.cursor + 1);
      const next = [...truncated, action.blocks].slice(-MAX_HISTORY);
      return { stack: next, cursor: next.length - 1 };
    }
    case 'UNDO':
      if (state.cursor <= 0) return state;
      return { ...state, cursor: state.cursor - 1 };
    case 'REDO':
      if (state.cursor >= state.stack.length - 1) return state;
      return { ...state, cursor: state.cursor + 1 };
    case 'RESET':
      return { stack: [action.blocks], cursor: 0 };
    default:
      return state;
  }
}

/**
 * Manages an undo/redo stack for the Visual Builder block tree.
 * Each entry is a complete snapshot of the block list at a point in time.
 * Uses useReducer to keep cursor and stack updates atomic.
 */
export function useBuilderHistory(initialBlocks: CmsBlockSnapshot[]) {
  const [state, dispatch] = useReducer(historyReducer, {
    stack: [initialBlocks],
    cursor: 0,
  });

  const current = state.stack[state.cursor] ?? [];
  const canUndo = state.cursor > 0;
  const canRedo = state.cursor < state.stack.length - 1;

  const push = useCallback((blocks: CmsBlockSnapshot[]) => {
    dispatch({ type: 'PUSH', blocks });
  }, []);

  const undo = useCallback((): CmsBlockSnapshot[] | null => {
    if (state.cursor <= 0) return null;
    dispatch({ type: 'UNDO' });
    return state.stack[state.cursor - 1] ?? null;
  }, [state.cursor, state.stack]);

  const redo = useCallback((): CmsBlockSnapshot[] | null => {
    if (state.cursor >= state.stack.length - 1) return null;
    dispatch({ type: 'REDO' });
    return state.stack[state.cursor + 1] ?? null;
  }, [state.cursor, state.stack]);

  const reset = useCallback((blocks: CmsBlockSnapshot[]) => {
    dispatch({ type: 'RESET', blocks });
  }, []);

  return { current, canUndo, canRedo, push, undo, redo, reset };
}
